import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const VenueCard = ({ venue, onSelect, onBooking, isSelected }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(amount);
  };

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance?.toFixed(1)}km`;
  };

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available': return 'text-success';
      case 'busy': return 'text-warning';
      case 'unavailable': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getAvailabilityText = (status) => {
    switch (status) {
      case 'available': return 'Disponível';
      case 'busy': return 'Ocupada';
      case 'unavailable': return 'Indisponível';
      default: return 'Verificando...';
    }
  };

  const handleImageNavigation = (direction) => {
    if (direction === 'next') {
      setCurrentImageIndex((prev) => 
        prev === venue?.images?.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) => 
        prev === 0 ? venue?.images?.length - 1 : prev - 1
      );
    }
  };

  const visibleAmenities = showAllAmenities ? venue?.amenities : venue?.amenities?.slice(0, 3);

  return (
    <div className={`bg-card border rounded-lg shadow-elevation-1 hover:shadow-elevation-2 transition-standard overflow-hidden ${
      isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
    }`}>
      {/* Image Gallery */}
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={venue?.images?.[currentImageIndex]}
          alt={venue?.imageAlts?.[currentImageIndex]}
          className="w-full h-full object-cover"
        />
        
        {/* Image Navigation */}
        {venue?.images?.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleImageNavigation('prev')}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleImageNavigation('next')}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
              {venue?.images?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-standard ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Availability Badge */}
        <div className="absolute top-3 right-3">
          <div className={`px-2 py-1 rounded-full text-xs font-medium bg-card/90 ${getAvailabilityColor(venue?.availability)}`}>
            {getAvailabilityText(venue?.availability)}
          </div>
        </div>

        {/* Favorite Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-3 left-3 bg-black/50 hover:bg-black/70 text-white"
        >
          <Icon name="Heart" size={16} />
        </Button>
      </div>
      {/* Content */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-card-foreground truncate">
              {venue?.name}
            </h3>
            <p className="text-sm text-muted-foreground truncate">
              {venue?.address}
            </p>
          </div>
          <div className="ml-3 text-right">
            <div className="text-lg font-bold text-primary font-data">
              {formatCurrency(venue?.pricePerHour)}
            </div>
            <div className="text-xs text-muted-foreground">por hora</div>
          </div>
        </div>

        {/* Rating and Distance */}
        <div className="flex items-center space-x-4 mb-3">
          <div className="flex items-center space-x-1">
            <Icon name="Star" size={14} className="text-warning fill-current" />
            <span className="text-sm font-medium">{venue?.rating}</span>
            <span className="text-sm text-muted-foreground">
              ({venue?.reviewCount} avaliações)
            </span>
          </div>
          {venue?.distance && (
            <>
              <span className="text-muted-foreground">•</span>
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {formatDistance(venue?.distance)}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Court Type */}
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="Grass" size={14} className="text-success" />
          <span className="text-sm font-medium text-card-foreground">
            {venue?.courtType}
          </span>
          <span className="text-sm text-muted-foreground">
            • {venue?.capacity} jogadores
          </span>
        </div>

        {/* Amenities */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {visibleAmenities?.map((amenity, index) => (
              <div
                key={index}
                className="flex items-center space-x-1 px-2 py-1 bg-muted rounded-full"
              >
                <Icon name={amenity?.icon} size={12} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {amenity?.name}
                </span>
              </div>
            ))}
            {venue?.amenities?.length > 3 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowAllAmenities(!showAllAmenities)}
                className="text-primary"
              >
                {showAllAmenities ? 'Ver menos' : `+${venue?.amenities?.length - 3} mais`}
              </Button>
            )}
          </div>
        </div>

        {/* Next Available Slot */}
        {venue?.nextAvailable && (
          <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} className="text-success" />
              <span className="text-sm font-medium text-success">
                Próximo horário disponível
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {venue?.nextAvailable?.date} às {venue?.nextAvailable?.time}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            fullWidth
            onClick={() => onSelect(venue)}
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
          >
            Ver Detalhes
          </Button>
          <Button
            variant="default"
            fullWidth
            onClick={() => onBooking(venue)}
            iconName="Calendar"
            iconPosition="left"
            iconSize={16}
            disabled={venue?.availability === 'unavailable'}
          >
            Reservar
          </Button>
        </div>

        {/* Owner Info */}
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={12} color="white" />
            </div>
            <span className="text-sm text-muted-foreground">
              Proprietário: {venue?.owner?.name}
            </span>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={12} className="text-warning fill-current" />
              <span className="text-xs text-muted-foreground">
                {venue?.owner?.rating}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueCard;